import { type FC, useState } from 'react';
import Button from 'form5/react/Button';
import Form, { type OnSubmit } from 'form5/react/Form';
import Field from 'form5/react/Field';

import { SubmitButton } from '…/app/common/SubmitButton/SubmitButton.jsx';
import { Dialog } from '…/app/w/workspace/common/Dialog/Dialog.tsx';

import { storeHivebriteCredentials } from './queries/storeIntegrationCredentials.op.mts';
import { searchIntegrationList } from '…/app/w/workspace/audiences/audience/members/hivebrite/queries/searchIntegrationList.op.mts';
import { HivebriteSegmentType } from '…/app/w/workspace/audiences/audience/members/hivebrite/types.js';

import classes from './HivebriteCredentialsDialog.module.css';

interface HivebriteCredentialsDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  integrationId?: string;
  setRefresh: (refresh: boolean) => void;
}

export const HivebriteCredentialsDialog: FC<HivebriteCredentialsDialogProps> = ({
  open,
  setOpen,
  setRefresh,
}) => {
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [errors, setErrors] = useState<unknown | null>(null);
  const [isVerifying, setIsVerifying] = useState<boolean>(false);
  const [verifySuccess, setVerifySuccess] = useState<boolean | null>(null);

  const handleSubmit: OnSubmit = async (data) => {
    setIsSaving(true);
    storeHivebriteCredentials({
      apiKey: data.refreshToken as string,
      apiUrl: data.apiUrl as string,
      clientId: data.clientId as string,
      clientSecret: data.clientSecret as string,
    })
      .then(async (integrationId: string) => {
        if (integrationId) {
          setSuccess(true);
          await handleVerify(integrationId);
        }
      })
      .catch(setErrors)
      .finally(() => {
        setRefresh(true);
        setIsSaving(false);
      });
  };

  const handleVerify = async (integrationId: string) => {
    setIsVerifying(true);
    setVerifySuccess(null);

    try {
      await searchIntegrationList(integrationId, 'group' as HivebriteSegmentType);
      setVerifySuccess(true);
    } catch (error) {
      setVerifySuccess(false);
      setErrors(error);
    } finally {
      setIsVerifying(false);
    }
  };

  const onClose = () => {
    setSuccess(false);
    setIsSaving(false);
    setErrors(null);
    setVerifySuccess(null);
    setOpen(false);
    // TODO: Hacky workaround
    if (window) window.location.reload();
  };

  return (
    <Dialog
      backdropped
      open={!!open}
      setOpen={onClose}
    >
      {!success
        ? (
          <Form
            className="w-full space-y-2"
            name="hivebrite-credentials"
            onSubmit={handleSubmit}
          >
            <h2>Configure Hivebrite Integration</h2>

            <p>Enter your Hivebrite credentials to connect to your Hivebrite account.</p>

            <div className={classes.FieldWrapper}>
              <label htmlFor="refreshToken">Refresh Token</label>

              <Field
                arrangement={Field.ARRANGEMENTS.STAND_ALONE}
                className={classes.FullWidthField}
                id="refreshToken"
                label={null}
                name="refreshToken"
                placeholder="Enter your Hivebrite refresh token"
                required
                type="text"
              />
            </div>

            <div className={classes.FieldWrapper}>
              <label htmlFor="clientId">Client ID</label>

              <Field
                arrangement={Field.ARRANGEMENTS.STAND_ALONE}
                className={classes.FullWidthField}
                id="clientId"
                label={null}
                name="clientId"
                placeholder="Enter your Hivebrite client ID"
                required
                type="text"
              />
            </div>

            <div className={classes.FieldWrapper}>
              <label htmlFor="clientSecret">Client Secret</label>

              <Field
                arrangement={Field.ARRANGEMENTS.STAND_ALONE}
                className={classes.FullWidthField}
                id="clientSecret"
                label={null}
                name="clientSecret"
                placeholder="Enter your Hivebrite client secret"
                required
                type="text"
              />
            </div>

            <div className={classes.FieldWrapper}>
              <label htmlFor="apiUrl">API URL</label>

              <Field
                arrangement={Field.ARRANGEMENTS.STAND_ALONE}
                className={classes.FullWidthField}
                id="apiUrl"
                label={null}
                name="apiUrl"
                placeholder="https://<your-subdomain>.<us|eu>.hivebrite.com/api"
                required
                type="text"
              />
            </div>

            {errors && (
              <div className={classes.ErrorMessage}>
                {errors instanceof Error ? errors.message : String(errors)}
              </div>
            )}

            <SubmitButton
              appearance={Button.APPEARANCES.AFFIRMING}
              disabled={isSaving}
              isSubmitting={isSaving}
              type="submit"
            >
              Save Credentials
            </SubmitButton>
          </Form>
        )
        : (
          <div className="space-y-4">
            <h2>Successfully Connected</h2>

            <p>Your Hivebrite integration has been successfully configured.</p>

            {verifySuccess === true && (
              <div className="text-green-600 py-2">
                Connection verified successfully! Your integration is working correctly.
              </div>
            )}

            {verifySuccess === false && (
              <div className={classes.ErrorMessage}>
                Failed to verify connection. Please check your credentials and try again.
              </div>
            )}

            <div className="flex flex-row gap-2">
              <Button
                appearance={Button.APPEARANCES.AFFIRMING}
                className="w-full"
                disabled={true}
                onClick={() => null}
              >{isVerifying ? 'Verifying...' : 'Verify Connection'}
              </Button>

              <Button
                className="w-full"
                disabled={isVerifying}
                onClick={onClose}
              >
                Close
              </Button>
            </div>
          </div>
        )}
    </Dialog>
  );
};

HivebriteCredentialsDialog.displayName = 'HivebriteCredentialsDialog';
